import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Send, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Community() {
  const { user } = useAuth();
  const [tab, setTab] = useState("questions");
  const [questions, setQuestions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);
  const [aiModel, setAiModel] = useState("gpt-5");
  const [qForm, setQForm] = useState({ title: "", body: "" });
  const [pForm, setPForm] = useState({ title: "", body: "" });
  const [aBody, setABody] = useState("");

  const load = useCallback(async () => {
    const [q, p] = await Promise.all([api.get("/questions"), api.get("/posts")]);
    setQuestions(q.data); setPosts(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openQ = async (qid) => { const r = await api.get(`/questions/${qid}`); setActive(r.data); };

  const askQ = async () => {
    if (!user) return toast.error("Please login");
    await api.post("/questions", qForm); setQForm({ title: "", body: "" }); toast.success("Question posted"); load();
  };
  const postP = async () => {
    if (!user) return toast.error("Please login");
    await api.post("/posts", { ...pForm, tags: [] }); setPForm({ title: "", body: "" }); toast.success("Post created"); load();
  };
  const askAI = async () => {
    if (!active) return;
    toast.info("AI is thinking…");
    try { await api.post("/answers/ai", { question_id: active.question_id, model: aiModel }); openQ(active.question_id); }
    catch(e) { toast.error(e?.response?.data?.detail || "AI failed"); }
  };
  const submitAnswer = async () => {
    if (!aBody) return;
    await api.post("/answers", { question_id: active.question_id, body: aBody });
    setABody(""); openQ(active.question_id);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="community-page">
      <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">Community</div>
      <h1 className="font-display text-4xl font-black text-slate-900 mb-6">Ask, post, debate finance.</h1>

      <div className="flex gap-2 mb-6">
        <Button variant={tab==="questions" ? "default" : "outline"} onClick={()=>setTab("questions")} data-testid="tab-questions">Questions</Button>
        <Button variant={tab==="posts" ? "default" : "outline"} onClick={()=>setTab("posts")} data-testid="tab-posts">Posts</Button>
      </div>

      {tab === "questions" ? (
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5 space-y-4">
            <div className="border border-slate-200 rounded p-5 bg-white" data-testid="ask-q-card">
              <h3 className="font-bold mb-2">Ask a question</h3>
              <Input className="mb-2" placeholder="Title" value={qForm.title} onChange={e=>setQForm({...qForm, title:e.target.value})} data-testid="q-title"/>
              <Textarea placeholder="Details…" value={qForm.body} onChange={e=>setQForm({...qForm, body:e.target.value})} data-testid="q-body"/>
              <Button className="btn-primary mt-3" onClick={askQ} data-testid="ask-q-btn"><Send className="w-4 h-4 mr-1"/>Ask</Button>
            </div>
            <div className="space-y-2">
              {questions.map(q => (
                <button key={q.question_id} onClick={()=>openQ(q.question_id)}
                  className={`w-full text-left border rounded p-4 bg-white hover:border-slate-900 ${active?.question_id===q.question_id ? 'border-slate-900' : 'border-slate-200'}`}
                  data-testid={`q-item-${q.question_id}`}>
                  <div className="font-semibold text-slate-900">{q.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{q.author_name} · {q.author_role}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-7">
            {!active ? <div className="text-slate-500 text-sm">Select a question to view answers.</div> : (
              <div className="border border-slate-200 rounded p-6 bg-white">
                <h3 className="font-display text-2xl font-black text-slate-900">{active.title}</h3>
                <p className="text-sm text-slate-600 mt-2">{active.body}</p>

                <div className="mt-5 flex items-center gap-2">
                  <Select value={aiModel} onValueChange={setAiModel}>
                    <SelectTrigger className="w-44" data-testid="ai-model-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-5">GPT-5</SelectItem>
                      <SelectItem value="claude">Claude Sonnet 4.5</SelectItem>
                      <SelectItem value="gemini">Gemini 3 Flash</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={askAI} className="btn-accent" data-testid="ask-ai-btn"><Sparkles className="w-4 h-4 mr-1"/>Get AI answer</Button>
                </div>

                <div className="mt-6 space-y-4">
                  {(active.answer_list || []).map(a => (
                    <div key={a.answer_id}
                         className={`p-4 rounded border ${a.is_ai ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}
                         data-testid={`ans-${a.answer_id}`}>
                      <div className="text-xs font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                        {a.is_ai && <Sparkles className="w-3 h-3 text-blue-600"/>}
                        <span className={a.is_ai ? "text-blue-700" : "text-slate-600"}>{a.author_name}</span>
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{a.body}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <Textarea placeholder="Write your answer…" value={aBody} onChange={e=>setABody(e.target.value)} data-testid="answer-body"/>
                  <Button className="btn-primary mt-2" onClick={submitAnswer} data-testid="submit-answer-btn"><MessageCircle className="w-4 h-4 mr-1"/>Post answer</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <div className="border border-slate-200 rounded p-5 bg-white">
              <h3 className="font-bold mb-2">Write a post</h3>
              <Input className="mb-2" placeholder="Title" value={pForm.title} onChange={e=>setPForm({...pForm, title:e.target.value})} data-testid="p-title"/>
              <Textarea placeholder="Body…" value={pForm.body} onChange={e=>setPForm({...pForm, body:e.target.value})} data-testid="p-body"/>
              <Button className="btn-primary mt-3" onClick={postP} data-testid="post-btn">Publish</Button>
            </div>
          </div>
          <div className="md:col-span-7 space-y-3">
            {posts.map(p => (
              <div key={p.post_id} className="border border-slate-200 rounded p-5 bg-white" data-testid={`post-${p.post_id}`}>
                <div className="font-bold text-slate-900">{p.title}</div>
                <div className="text-xs text-slate-500 mb-2">{p.author_name} · {p.author_role}</div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.body}</p>
              </div>
            ))}
            {posts.length === 0 && <div className="text-sm text-slate-500">No posts yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
